import { straicoClient, MODEL_MAPPING } from './straicoService';

export const generateInterviewQuestions = async ({ jobData, formData, categories, count = 15 }) => {
  const prompt = buildInterviewPrompt({ jobData, formData, categories, count });
  
  try {
    const model = MODEL_MAPPING[formData?.selectedModel] || 'openai/gpt-4o-mini';
    
    const requestBody = {
      model: model,
      message: prompt,
      temperature: 0.8,
      max_tokens: 3000
    };

    const response = await straicoClient.post('/prompt/completion', requestBody);
    
    let content = '';
    if (response.data && response.data.completion) {
      content = response.data.completion.trim();
    } else if (response.data && response.data.data) {
      const responseData = response.data.data;
      if (responseData.completions) {
        const completions = responseData.completions;
        const modelKey = Object.keys(completions)[0];
        const completion = completions[modelKey];
        if (completion && completion.completion && completion.completion.choices) {
          content = completion.completion.choices[0].message.content;
        }
      }
    } else if (response.data && response.data.choices) {
      content = response.data.choices[0].message.content;
    }

    if (!content) {
      throw new Error('No content received from AI service');
    }

    return parseInterviewQuestions(content, categories);
    
  } catch (error) {
    console.error('Interview questions generation error:', error);
    
    // Fallback to mock questions if API fails
    if (error.response?.status >= 400) {
      console.log('API failed, generating mock questions...');
      return generateMockInterviewQuestions({ jobData, formData, categories, count });
    }
    
    throw error;
  }
};

const buildInterviewPrompt = ({ jobData, formData, categories, count }) => {
  const jobTitle = formData?.jobTitle || 'the position';
  const jobDescription = jobData || formData?.jobDescription || '';
  
  let prompt = `You are an expert HR professional and interview specialist. Generate a comprehensive set of ${count} interview questions for the position of "${jobTitle}" at the City of Metropolis.

Job Context:
${jobDescription ? `Job Description: ${jobDescription}` : `Position: ${jobTitle}`}
${formData?.location ? `Location: ${formData.location}` : ''}
${formData?.seniority ? `Seniority Level: ${formData.seniority}` : ''}
${formData?.workMode ? `Work Mode: ${formData.workMode}` : ''}

Generate questions in the following categories: ${categories.join(', ')}

Requirements:
1. Create questions that are specific to this role and the City of Metropolis context
2. Include a mix of difficulty levels appropriate for the seniority level
3. Ensure questions assess both technical competency and cultural fit
4. Format questions clearly and professionally
5. Distribute questions across the selected categories

Category Guidelines:
- General: Role understanding, motivation, career goals, basic qualifications
- Technical: Job-specific skills, tools, methodologies, problem-solving scenarios
- Behavioral: Past experiences, teamwork, conflict resolution, leadership examples
- Leadership: Management style, team building, decision-making, delegation
- Problem-solving: Critical thinking, analytical skills, creative solutions

Output Format:
For each category, provide:
CATEGORY NAME:
1. [Question 1]
2. [Question 2]
...

Make questions engaging, relevant, and designed to reveal the candidate's suitability for municipal government work and public service.`;

  return prompt;
};

const parseInterviewQuestions = (content, categories) => {
  const lines = content.split('\n').filter(line => line.trim());
  const categorizedQuestions = {};
  let currentCategory = null;
  
  // Initialize categories
  categories.forEach(cat => {
    categorizedQuestions[cat] = [];
  });
  
  for (const line of lines) {
    const trimmedLine = line.trim();
    
    // Check if this is a category header
    const categoryMatch = categories.find(cat => 
      trimmedLine.toLowerCase().includes(cat.toLowerCase()) && 
      (trimmedLine.includes(':') || trimmedLine.includes('Questions'))
    );
    
    if (categoryMatch) {
      currentCategory = categoryMatch;
      continue;
    }
    
    // Check if this is a numbered question
    const questionMatch = trimmedLine.match(/^\d+\.\s*(.+)$/);
    if (questionMatch && currentCategory) {
      const question = questionMatch[1].trim();
      if (question.length > 10) { // Filter out very short/invalid questions
        categorizedQuestions[currentCategory].push(question);
      }
    }
  }
  
  // If parsing failed, try alternative parsing
  if (Object.values(categorizedQuestions).every(arr => arr.length === 0)) {
    return parseAlternativeFormat(content, categories);
  }
  
  return {
    categorizedQuestions,
    totalQuestions: Object.values(categorizedQuestions).reduce((sum, arr) => sum + arr.length, 0),
    categories: categories
  };
};

const parseAlternativeFormat = (content, categories) => {
  const lines = content.split('\n').filter(line => line.trim());
  const categorizedQuestions = {};
  
  // Initialize categories
  categories.forEach(cat => {
    categorizedQuestions[cat] = [];
  });
  
  // Extract all questions regardless of format
  const allQuestions = [];
  for (const line of lines) {
    const trimmedLine = line.trim();
    // Look for questions (lines ending with ? or containing question patterns)
    if (trimmedLine.includes('?') || 
        trimmedLine.match(/^(How|What|Why|When|Where|Can you|Tell me|Describe|Explain)/i)) {
      const cleanQuestion = trimmedLine.replace(/^\d+\.\s*/, '').replace(/^-\s*/, '').trim();
      if (cleanQuestion.length > 10) {
        allQuestions.push(cleanQuestion);
      }
    }
  }
  
  // Distribute questions across categories
  const questionsPerCategory = Math.ceil(allQuestions.length / categories.length);
  categories.forEach((category, index) => {
    const startIndex = index * questionsPerCategory;
    const endIndex = Math.min(startIndex + questionsPerCategory, allQuestions.length);
    categorizedQuestions[category] = allQuestions.slice(startIndex, endIndex);
  });
  
  return {
    categorizedQuestions,
    totalQuestions: allQuestions.length,
    categories: categories
  };
};

const generateMockInterviewQuestions = ({ jobData, formData, categories, count }) => {
  const jobTitle = formData?.jobTitle || 'Municipal Employee';
  const isLeadership = formData?.seniority === 'senior' || formData?.seniority === 'lead' || formData?.seniority === 'executive';
  
  const questionBank = {
    general: [
      `What interests you most about working for the City of Metropolis?`,
      `How does this ${jobTitle} position align with your career goals?`,
      `What do you know about our city's current priorities and initiatives?`,
      `Why are you interested in public service?`,
      `What unique value would you bring to this role?`,
      `How do you stay current with industry trends and best practices?`,
      `What motivates you to do your best work?`
    ],
    technical: [
      `Describe your experience with the key technical skills required for this position.`,
      `Walk me through your approach to [specific technical task related to the role].`,
      `How would you handle a situation where you need to learn a new system or technology quickly?`,
      `What tools or software have you used that would be relevant to this position?`,
      `Describe a complex project you've worked on and your role in its success.`,
      `How do you ensure quality and accuracy in your technical work?`,
      `What's your experience with data analysis and reporting?`
    ],
    behavioral: [
      `Tell me about a time when you had to work with a difficult team member.`,
      `Describe a situation where you had to meet a tight deadline with limited resources.`,
      `Give me an example of when you had to adapt to significant changes at work.`,
      `Tell me about a time when you made a mistake. How did you handle it?`,
      `Describe a situation where you had to explain complex information to someone without technical knowledge.`,
      `How do you prioritize multiple competing tasks and deadlines?`,
      `Tell me about a time when you went above and beyond your job requirements.`
    ],
    leadership: [
      `Describe your management or leadership style.`,
      `How do you motivate team members who are struggling with performance?`,
      `Tell me about a time when you had to make an unpopular decision.`,
      `How do you handle conflict within your team?`,
      `Describe how you would build relationships with other city departments.`,
      `How do you ensure effective communication across different levels of the organization?`,
      `What's your approach to developing and mentoring team members?`
    ],
    'problem-solving': [
      `Describe a complex problem you solved in your previous role.`,
      `How do you approach analyzing and breaking down large, complicated issues?`,
      `Tell me about a time when you had to think creatively to solve a problem.`,
      `How would you handle a situation where you don't have all the information you need?`,
      `Describe your decision-making process when facing multiple solution options.`,
      `How do you involve others when solving complex problems?`,
      `Tell me about a time when your first solution didn't work. What did you do?`
    ]
  };
  
  const categorizedQuestions = {};
  const questionsPerCategory = Math.ceil(count / categories.length);
  
  categories.forEach(category => {
    const availableQuestions = questionBank[category] || questionBank.general;
    const selectedQuestions = availableQuestions
      .sort(() => 0.5 - Math.random())
      .slice(0, Math.min(questionsPerCategory, availableQuestions.length));
    
    categorizedQuestions[category] = selectedQuestions;
  });
  
  return {
    categorizedQuestions,
    totalQuestions: Object.values(categorizedQuestions).reduce((sum, arr) => sum + arr.length, 0),
    categories: categories,
    isMock: true
  };
};

export default { generateInterviewQuestions };